import { gql } from '@apollo/client';

import { assertUnreachableOrReturnDefault } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

export const GetTaskDisplayStatusTaskFragment = gql`
  fragment GetTaskDisplayStatusTaskFragment on CheckWorkflowTask {
    id
    status
    ... on LintCheckTask {
      id
      result {
        diagnostics {
          level
        }
        stats {
          ignoredCount
        }
      }
    }
    ... on CustomCheckTask {
      id
      result {
        violations {
          level
        }
      }
    }
    ... on ProposalsCheckTask {
      id
      proposalCoverage
      didOverrideProposalsCheckTask
      severityLevel
      hasWarnings @include(if: $proposalsUseHasWarnings)
      diffs @skip(if: $proposalsUseHasWarnings) {
        hasApprovedProposal
        diffItem {
          __typename
        }
      }
    }
    ... on OperationsCheckTask {
      result {
        id
        crossedOperationThreshold
      }
    }
    ... on DownstreamCheckTask {
      results {
        failsUpstreamWorkflow
        downstreamWorkflow {
          id
          status
        }
      }
    }
  }
`;

export function taskHasWarnings(
  task: GraphQLTypes.GetTaskDisplayStatusTaskFragment,
  shouldDescriptionsWarn: boolean,
  proposalsUseHasWarnings: boolean = false,
): boolean {
  if (task.status !== GraphQLTypes.CheckWorkflowTaskStatus.PASSED) {
    return false;
  } else {
    switch (task.__typename) {
      case 'CompositionCheckTask':
      case 'FilterCheckTask':
        return false;
      case 'DownstreamCheckTask':
        return !!task.results?.some(
          ({ failsUpstreamWorkflow, downstreamWorkflow }) => {
            return (
              downstreamWorkflow?.status ===
                GraphQLTypes.CheckWorkflowStatus.FAILED &&
              !failsUpstreamWorkflow
            );
          },
        );
      case 'OperationsCheckTask':
        return !!(
          task.result?.crossedOperationThreshold !== null &&
          task.result?.crossedOperationThreshold !== undefined
        );
      case 'CustomCheckTask':
        return !!task.result?.violations.some(
          (violation) =>
            violation.level === GraphQLTypes.ViolationLevel.WARNING,
        );
      case 'LintCheckTask':
        return !!task.result?.diagnostics?.some(
          (violation) =>
            violation.level === GraphQLTypes.LintDiagnosticLevel.WARNING,
        );
      case 'ProposalsCheckTask':
        if (proposalsUseHasWarnings) {
          return !task.didOverrideProposalsCheckTask && !!task.hasWarnings;
        }
        return (
          !task.didOverrideProposalsCheckTask &&
          ((task.severityLevel ===
            GraphQLTypes.ProposalChangeMismatchSeverity.WARN &&
            task.proposalCoverage !== GraphQLTypes.ProposalCoverage.FULL) ||
            (shouldDescriptionsWarn &&
              !!task.diffs?.some(
                (diff) =>
                  !diff.hasApprovedProposal &&
                  diff.diffItem.__typename === 'FlatDiffChangeDescription',
              )))
        );
      default:
        return assertUnreachableOrReturnDefault(task, false);
    }
  }
}
export function shouldDisplayTask(
  task: GraphQLTypes.GetTaskDisplayStatusTaskFragment,
  shouldDescriptionsWarn: boolean,
  proposalsUseHasWarnings: boolean,
): boolean {
  switch (task.status) {
    case GraphQLTypes.CheckWorkflowTaskStatus.FAILED:
    case GraphQLTypes.CheckWorkflowTaskStatus.PENDING:
      return true;
    case GraphQLTypes.CheckWorkflowTaskStatus.BLOCKED:
      return false;
    case GraphQLTypes.CheckWorkflowTaskStatus.PASSED:
      return taskHasWarnings(
        task,
        shouldDescriptionsWarn,
        proposalsUseHasWarnings,
      );
    default:
      return assertUnreachableOrReturnDefault(task.status, true);
  }
}
